import { ThemeProvider, useTheme } from 'next-themes';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

type Props = {
  children: React.ReactNode;
};

type ThemeContextType = {
  themeName: string;
  toggleTheme: () => void;
  isLight: boolean;
  isDark: boolean;
};

const ThemeContext = createContext<ThemeContextType | null>(null);

export const useAppTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useAppTheme must be used within ThemeContainer');
  }
  return context;
};

function ThemeContextWrapper({ children }: { children: React.ReactNode }) {
  const { theme, setTheme } = useTheme();
  const [_mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(theme === 'light' ? 'dark' : 'light');
  }, [theme, setTheme]);

  const contextValue = useMemo(
    () => ({
      themeName: theme || 'light',
      toggleTheme,
      isLight: theme === 'light',
      isDark: theme === 'dark',
    }),
    [theme, toggleTheme],
  );

  // Since we apply the theme class immediately in the HTML head,
  // we don't need to hide content during initialization
  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
}

function ThemeContainer({ children }: Props) {
  return (
    <ThemeProvider
      attribute="class"
      defaultTheme="light"
      enableSystem={false}
      storageKey="appTheme"
      themes={['light', 'dark']}
    >
      <ThemeContextWrapper>{children}</ThemeContextWrapper>
    </ThemeProvider>
  );
}

export default ThemeContainer;
